import {
  WORKFLOW_RUN_GATE_ASK_ID_PREFIX,
  WORKFLOW_RUN_STEP_ASK_ID_PREFIX,
} from '@digital-twins/shared-types'

/**
 * The study pin sits at the END of the current exchange while a run is
 * live, but the run's step-handoff and gate-decision lines are dated to the
 * step that produced them, not to the end of the run. Left in the turns
 * column, every handoff renders above ALL of the run's cards instead of
 * between the cards of the steps it separates ("News research" is directly
 * followed by "Write the morning brief" with no transition). This module
 * pulls those lines out of the column so `ThreadInlineArtifacts` can
 * interleave them with the step groups they belong next to.
 *
 * Both prefixes are declared in shared-types (`workflow-run-ask-ids.ts`),
 * the same module the backend lane writes them from.
 */
export {
  WORKFLOW_RUN_GATE_ASK_ID_PREFIX,
  WORKFLOW_RUN_STEP_ASK_ID_PREFIX,
}

export interface StepNoteTurn {
  id: string
  role: string
  askId?: string | null
}

/**
 * Where a note sits relative to the step group it belongs to. Handoff
 * narration (`workflow-run-step:`) reads as the assistant's lead-in to the
 * step, so it renders BEFORE that step's group. A gate decision
 * (`workflow-run-gate:`) records what happened to the step's output, so it
 * renders AFTER the group. Both fall back to the after-anchor when the step
 * produced no group, so a note is never dropped.
 */
export type WorkflowRunStepNotePlacement = 'before' | 'after'

export interface WorkflowRunStepNote<T extends StepNoteTurn> {
  stepIndex: number
  placement: WorkflowRunStepNotePlacement
  /**
   * The revision attempt this note describes (0 when the ask id carries no
   * attempt segment). Step 6 of
   * `docs/plans/2026-09-05-revise-step-under-review-from-chat.md` (D9):
   * `ArtifactPreviewCard`'s `bucketStepNotes` keeps only the highest-attempt
   * `before` note per step index, since the earlier one describes a document
   * that no longer exists.
   */
  attempt: number
  turn: T
}

/** Placement derived from the ask-id prefix; null for a non-run ask id. */
export function placementFromWorkflowRunAskId(
  askId: string | null | undefined,
): WorkflowRunStepNotePlacement | null {
  if (!askId) {
    return null
  }
  if (askId.startsWith(WORKFLOW_RUN_STEP_ASK_ID_PREFIX)) {
    return 'before'
  }
  if (askId.startsWith(WORKFLOW_RUN_GATE_ASK_ID_PREFIX)) {
    return 'after'
  }
  return null
}

interface ParsedWorkflowRunAskId {
  stepIndex: number
  /** 0 when the ask id carries no `:<attempt>` segment. */
  attempt: number
}

/**
 * `<stepIndex>` or `<stepIndex>:<attempt>` after `<prefix><runToken>:`.
 * `workflow-run-step:<token>:<index>` is attempt 0; `...:<index>:<attempt>`
 * is a revised attempt (D9). Null for anything else: a different run token,
 * a non-numeric remainder, or an unknown prefix.
 */
function parseWorkflowRunAskId(
  askId: string,
  runToken: string,
  prefix: string,
): ParsedWorkflowRunAskId | null {
  const tokenPrefix = `${prefix}${runToken}:`
  if (!askId.startsWith(tokenPrefix)) {
    return null
  }
  const remainder = askId.slice(tokenPrefix.length)
  const match = /^(\d+)(?::(\d+))?$/.exec(remainder)
  if (!match) {
    return null
  }
  const stepIndex = Number.parseInt(match[1], 10)
  if (!Number.isFinite(stepIndex) || stepIndex < 0) {
    return null
  }
  const attempt = match[2] === undefined ? 0 : Number.parseInt(match[2], 10)
  if (!Number.isFinite(attempt) || attempt < 0) {
    return null
  }
  return { stepIndex, attempt }
}

function parseAnyWorkflowRunAskId(
  askId: string | null | undefined,
  runToken: string,
): ParsedWorkflowRunAskId | null {
  if (!askId || !runToken) {
    return null
  }
  for (const prefix of [
    WORKFLOW_RUN_STEP_ASK_ID_PREFIX,
    WORKFLOW_RUN_GATE_ASK_ID_PREFIX,
  ]) {
    const parsed = parseWorkflowRunAskId(askId, runToken, prefix)
    if (parsed) {
      return parsed
    }
  }
  return null
}

/**
 * Step index encoded in `workflow-run-step:<runToken>:<stepIndex>` (or its
 * `:<attempt>` variant) or `workflow-run-gate:<runToken>:<stepIndex>` for
 * THIS run token; null for any other ask id, another run's token, or a
 * non-numeric index.
 */
export function stepIndexFromWorkflowRunAskId(
  askId: string | null | undefined,
  runToken: string,
): number | null {
  return parseAnyWorkflowRunAskId(askId, runToken)?.stepIndex ?? null
}

/**
 * The revision attempt encoded in a `workflow-run-step:` or
 * `workflow-run-gate:` ask id for THIS run token; 0 for the base (attempt 0)
 * form, and 0 when the ask id does not match at all (a caller that already
 * checked `stepIndexFromWorkflowRunAskId` is not null gets a real attempt).
 */
export function attemptFromWorkflowRunAskId(
  askId: string | null | undefined,
  runToken: string,
): number {
  return parseAnyWorkflowRunAskId(askId, runToken)?.attempt ?? 0
}

/**
 * Splits the pinned run's step and gate lines out of the column so the study
 * pin can interleave them with the step's cards. Column order is preserved.
 * Only `assistant`-voiced turns count (gate lines are assistant-voiced too).
 */
export function collectWorkflowRunStepNotes<T extends StepNoteTurn>(
  turns: Array<T>,
  runToken: string | null | undefined,
): Array<WorkflowRunStepNote<T>> {
  if (!runToken) {
    return []
  }

  const notes: Array<WorkflowRunStepNote<T>> = []
  turns.forEach((turn) => {
    if (turn.role !== 'assistant') {
      return
    }
    const stepIndex = stepIndexFromWorkflowRunAskId(turn.askId, runToken)
    if (stepIndex === null) {
      return
    }
    notes.push({
      stepIndex,
      placement: placementFromWorkflowRunAskId(turn.askId) ?? 'after',
      attempt: attemptFromWorkflowRunAskId(turn.askId, runToken),
      turn,
    })
  })
  return notes
}

/**
 * The notes worth pinning under the run: for each step, only the note whose
 * attempt is the step's CURRENT attempt.
 *
 * A step that was revised keeps every earlier handoff line in the thread
 * (one per attempt), and while a redo is in flight the step's attempt is
 * already ahead of every line written so far. Pinning those would stack
 * stale narration under the newest turn, below the reader's own revision
 * message and the reply streaming above it. Dropped here means "stays in the
 * column at its own time", never "hidden": the callers only skip pinned ids.
 * A note for a step index the run does not know keeps today's pinning.
 */
export function currentAttemptWorkflowRunStepNotes<T extends StepNoteTurn>(
  notes: Array<WorkflowRunStepNote<T>>,
  steps: ReadonlyArray<{ stepIndex: number; attempt: number }>,
): Array<WorkflowRunStepNote<T>> {
  const attemptByStepIndex = new Map(
    steps.map((step) => [step.stepIndex, step.attempt]),
  )
  return notes.filter((note) => {
    const current = attemptByStepIndex.get(note.stepIndex)
    if (current === undefined) {
      return true
    }
    /* The base ask id (no `:<attempt>` segment, parsed as 0) is what the
       advancer writes for a step that was never revised, whose `attempt`
       is 1: steps are 1-based, revised lines carry the real number. */
    return note.attempt === 0 ? current <= 1 : current === note.attempt
  })
}
